import {
	Action,
	Dispatch,
} from 'redux';
// Import services
import {
	StringService,
} from '@app/services';
import {
	NotificationType,
	OverlayTarget,
} from '@app/enumerations';
// Import action type enumeration.
import ActionType from '@app/redux/actions/types';
// Import api models.
import {
	Peril,
	PolicyLoss
} from '@app/models/api';
// Import controller factory.
import ControllerFactoryProvider from '@app/controllers/factory';
// Import action methods.
import {
	addNotification,
	endProcessing,
	startProcessing,
} from '@app/redux/actions/methods';
// Import action parameters.
import {
	PolicyParameters,
} from '@app/redux/actions/parameters';


// Set Claim Action

interface SetClaimParameters {
	claim: PolicyLoss;
}

type SetClaimAction = Action & SetClaimParameters;

const setClaimAction: (claim: PolicyLoss) =>
	SetClaimAction = (claim: PolicyLoss) => ({
		claim,
		type: ActionType.SET_NEW_CLAIM
	});

const setClaim = (claim: PolicyLoss) =>
	async (dispatch: Dispatch): Promise<void> => {
		dispatch<SetClaimAction>(setClaimAction(claim));
	};

// Set Previous Claims Action

interface SetPreviousClaimsParameters {
	previousClaims: PolicyLoss[];
}

type SetPreviousClaimsAction = Action & SetPreviousClaimsParameters;

const setPreviousClaimsAction: (previousClaims: PolicyLoss[]) =>
	SetPreviousClaimsAction = (previousClaims: PolicyLoss[]) => ({
		previousClaims,
		type: ActionType.SET_PREVIOUS_CLAIMS
	});

const setPreviousClaims = (previousClaims: PolicyLoss[]) =>
	async (dispatch: Dispatch): Promise<void> => {
		dispatch<SetPreviousClaimsAction>(setPreviousClaimsAction(previousClaims));
	};


// Load Previous Claims Action.

interface LoadPreviousClaimsParameters extends PolicyParameters {
	zipCode?: string;
}

interface LoadPreviousClaimsAction extends Action {
	previousClaims: PolicyLoss[];
}

const loadPreviousClaimsAction: (previousClaims: PolicyLoss[]) =>
	LoadPreviousClaimsAction = (previousClaims: PolicyLoss[]) => ({
		previousClaims,
		type: ActionType.LOAD_PREVIOUS_CLAIMS
	});

const loadPreviousClaims = (params: LoadPreviousClaimsParameters) =>
	async (dispatch: Dispatch): Promise<PolicyLoss[]> => {
		const messageId: string = `${StringService.getInstance().hashObject(undefined)}-loadPreviousClaims`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading previous claims...',
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN,
			})(dispatch);

			// Call api to fetch previous claims.
			let previousClaims: PolicyLoss[] = params.zipCode === undefined
				? await ControllerFactoryProvider.getControllerFactory().getClaimsController().getPolicyLosses(params.policyId)
				: await ControllerFactoryProvider.getControllerFactory().getClaimsController().getPolicyLossesPublic(params.policyId, params.zipCode);
			
			dispatch<LoadPreviousClaimsAction>(loadPreviousClaimsAction(previousClaims));
       
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);

			return previousClaims;

		} catch (error: any) {
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);
			// Throw error.
			throw error;
        }
	};


// Load Claim Perils

interface LoadClaimPerilsParameters extends PolicyParameters {
	zipCode?: string;
}

interface LoadClaimPerilsAction extends Action {
	claimPerils: Peril[];
}

const loadClaimPerilsAction: (claimPerils: Peril[]) =>
	LoadClaimPerilsAction = (claimPerils: Peril[]) => ({
		claimPerils: claimPerils,
		type: ActionType.LOAD_CLAIM_PERILS
	});


const loadClaimPerils = (params: LoadClaimPerilsParameters) =>
	async (dispatch: Dispatch): Promise<Peril[]> => {
		const messageId: string = `${StringService.getInstance().hashObject(undefined)}-loadClaimPerils`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading policy perils...',
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN,
			})(dispatch);

			let claimPerils: Peril[] = params.zipCode === undefined
				? await ControllerFactoryProvider.getControllerFactory().getPolicyController().getPolicyPerils(params.policyId)
				: await ControllerFactoryProvider.getControllerFactory().getPolicyController().getPolicyPerilsPublic(params.policyId, params.zipCode);
			dispatch<LoadClaimPerilsAction>(loadClaimPerilsAction(claimPerils));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);

			return claimPerils;
		} catch (error: any) {
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);
			// Throw error.
			throw error;
		}
	};


// Submit Claim

interface SubmitClaimParameters extends PolicyParameters {
	claim: PolicyLoss,
	zipCode?: string;
}

const submitClaim = (params: SubmitClaimParameters) =>
	async (dispatch: Dispatch): Promise<PolicyLoss[]> => {
		const messageId: string = `${StringService.getInstance().hashObject(params)}-submitClaim`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Submitting claim...',
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN,
			})(dispatch);

			// Submit the claim.
			let policyLossId: string = params.zipCode === undefined
				? await ControllerFactoryProvider.getControllerFactory().getClaimsController().submitClaim(params.policyId, params.claim)
				: await ControllerFactoryProvider.getControllerFactory().getClaimsController().submitClaimPublic(params.policyId, params.zipCode, params.claim);

			// Load the previous claims after submitting the claim.
			let previousClaims: PolicyLoss[] = params.zipCode === undefined
				? await ControllerFactoryProvider.getControllerFactory().getClaimsController().getPolicyLosses(params.policyId)
				: await ControllerFactoryProvider.getControllerFactory().getClaimsController().getPolicyLossesPublic(params.policyId, params.zipCode);

			dispatch<LoadPreviousClaimsAction>(loadPreviousClaimsAction(previousClaims));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);

			return previousClaims;
		} catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);
			// Throw error.
			throw error;
		}
	};


// Export actions.
export {
	LoadClaimPerilsAction,
	LoadPreviousClaimsAction,
	SetClaimAction,
	SetPreviousClaimsAction
};

// Export action methods.
export {
	loadClaimPerils,
	loadPreviousClaims,
	setClaim,
	setPreviousClaims,
	submitClaim,
};

// Export action parameters.
export {
	LoadClaimPerilsParameters,
	LoadPreviousClaimsParameters,
	SetClaimParameters,
	SetPreviousClaimsParameters,
	SubmitClaimParameters,
};

// Export action factories.
export {
	loadClaimPerilsAction,
	loadPreviousClaimsAction,
	setClaimAction,
	setPreviousClaimsAction
};